import {useState, useCallback, useRef, useEffect, useContext} from 'react';
import {ScreenFormatContext} from "@mnjsplatform/data";

const STORAGE_NAME = "GridViewResizeStorage";

const getStorageItemName = ({tableName, columnName}) => {
    const name = [tableName, columnName].filter(i => i).join('_');
    return name || null;
}

const getGridViewResizeColumnStorage = ({ tableName, columnName }) => {
    try {
        const storageValue = localStorage.getItem(STORAGE_NAME);
        if (!storageValue) return null;

        const itemName = getStorageItemName({ tableName, columnName });
        if (!itemName) return null;

        const storage = JSON.parse(storageValue);
        if (!Array.isArray(storage)) return null;

        return storage.find(item => item.name === itemName) || null;

    } catch (error) {
        return null;
    }
};

const setGridViewResizeColumnStorage = ({ tableName, columnName, width }) => {
    if (!width) return;
    try {
        const storageValue = localStorage.getItem(STORAGE_NAME);

        const itemName = getStorageItemName({ tableName, columnName });
        if (itemName) {
            let storage = storageValue ? JSON.parse(storageValue) : [];
            if (Array.isArray(storage)) {
                storage = storage.filter(item => item.name !== itemName) || null;
                localStorage.setItem(STORAGE_NAME, JSON.stringify([...storage, {name: itemName, width  }]))
            }
        }
    } catch {
        /* Empty */
    }
};

const GridViewHeaderResizerComponent = ({ dataItem, columnName }) => {
    const [isActive, setIsActive] = useState(false);
    const elRef = useRef(null);
    const stateRef = useRef(null);
    const rowsCount = dataItem?.dataTable()?.length || 0;

    // Мемоизированные функции для предотвращения лишних пересозданий
    const getEventEl = useCallback(() => document.body, []);

    const initStateRef = ({target, clientX}) => {
        const parentEl = target?.closest("th");
        const table = target?.closest("table");

        if (!table || !parentEl) return;

        const col = table.querySelector(`colgroup col[name="${columnName}"]`);
        const bodyCells = table.querySelectorAll(`tbody td[name="${columnName}"]`);

        stateRef.current = {
            parentEl,
            table,
            col,
            bodyCells,
            width: parseInt(parentEl.style.width || parentEl.clientWidth, 10),
            startX: clientX | 0,
            tableName: table.getAttribute("name"),
            columnName
        };
    }

    const handleMouseMove = useCallback((e) => {
        if (stateRef.current) {
            const { startX, width: initialWidth } = stateRef.current;
            const dx = e.clientX - startX;
            const newWidth = Math.max(initialWidth + dx, 50); // Минимальная ширина 50px

            if (stateRef.current.parentEl) {
                stateRef.current.parentEl.style.width = `${newWidth}px`;
            }
            if (stateRef.current.bodyCells) {
                stateRef.current.bodyCells.forEach((cell) => {
                    cell.style.width = newWidth + "px";
                });
            }
            if (stateRef.current.col) {
                stateRef.current.col.style.width = `${newWidth}px`;
            }
        }
    }, []);

    const handleMouseUp = useCallback(() => {
        if (stateRef.current?.table) {
            stateRef.current.table.classList.remove("no-selection");
        }

        const el = getEventEl();
        el.removeEventListener("mouseup", handleMouseUp);
        el.removeEventListener("mousemove", handleMouseMove);

        setIsActive(false);

        const {tableName, columnName, parentEl} = stateRef.current;
        const width = parseInt(parentEl.style.width || parentEl.clientWidth, 10);

        setGridViewResizeColumnStorage({tableName, columnName, width});

        stateRef.current = null;
    }, [getEventEl, handleMouseMove]);

    const subscribe = useCallback(() => {
        const el = getEventEl();
        el.addEventListener("mouseup", handleMouseUp);
        el.addEventListener("mousemove", handleMouseMove);
    }, [getEventEl, handleMouseMove, handleMouseUp]);

    const handleMouseDown = useCallback((e) => {

        e.stopPropagation();
        e.preventDefault();

        const target = e.currentTarget;

        initStateRef({target, clientX:e.clientX});

        stateRef?.current?.table.classList.add("no-selection");

        setIsActive(true);
        subscribe();
    }, [columnName, subscribe]);

    // Очистка при размонтировании
    useEffect(() => {
        return () => {
            if (stateRef.current?.table) {
                stateRef.current.table.classList.remove("no-selection");
            }
            unsubscribe();
        };
    }, []);

    useEffect(() => {

        if (rowsCount && elRef?.current) {
            initStateRef({target: elRef?.current});
            setTimeout(updateSize, 0);
        }

    }, [rowsCount]);

    const unsubscribe = useCallback(() => {
        const el = getEventEl();
        el.removeEventListener("mouseup", handleMouseUp);
        el.removeEventListener("mousemove", handleMouseMove);
    }, [getEventEl, handleMouseMove, handleMouseUp]);

    const init = (el) => {
        if (!elRef?.current) {
            initStateRef({target: el});
            elRef.current = el;
            updateSize();
        }
    }

    const updateSize = () => {
        const {tableName, columnName} = stateRef.current;
        const item = getGridViewResizeColumnStorage({tableName, columnName})

        const bodyCells = stateRef.current.table.querySelectorAll(`tbody td[name="${columnName}"]`);

        if (item?.width) {
            if (stateRef.current.parentEl) {
                stateRef.current.parentEl.style.width = `${item?.width}px`;
            }
            if (bodyCells) {
                bodyCells.forEach((cell) => {
                    cell.style.width = item?.width + "px";
                });
            }
            if (stateRef.current.col) {
                stateRef.current.col.style.width = `${item?.width}px`;
            }
        }
    }

    return (
        <div
            ref={init}
            className={`resizer${isActive ? " active" : ""}`}
            onMouseDown={handleMouseDown}
            role="slider"
            aria-label={`Resize column ${columnName}`}
        />
    );
};

const GridViewHeaderResizer = ({ dataItem, columnName }) => {
    const isDesktop = useContext(ScreenFormatContext);

    if (!isDesktop) {
        return null;
    }

    return (
        <GridViewHeaderResizerComponent dataItem={dataItem} columnName={columnName} />
    );
}

export default GridViewHeaderResizer;